import React from 'react';
import { Dialog, DialogContent, Box, Typography, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import { DQ_PRO_PLAN_KEY } from '../../../utils/constants';
import { solutionPlanKey } from '../../../utils/productAccess';

const ORANGE = 'var(--brand-primary, #FF6900)';
const MUTED = 'var(--brand-text-muted, #6b6b6b)';
const BORDER = 'var(--brand-border, #e3e0dc)';

/** The three link targets, worded exactly as PW deck slides 48-50. */
export const LINK_TYPE = {
    LEARN_CREATOR: 'learn_creator',
    LEARNING_PROGRAM: 'learning_program',
    STANDARD_ASSESSMENT: 'standard_assessment',
};

const CARDS = [
    {
        key: LINK_TYPE.LEARN_CREATOR,
        title: 'LearnCreator',
        blurb: 'Give networks or schools access to build their own content',
    },
    {
        key: LINK_TYPE.LEARNING_PROGRAM,
        title: 'Learning Program',
        blurb: 'Link a ready-made program to this {target}',
    },
    {
        key: LINK_TYPE.STANDARD_ASSESSMENT,
        title: 'Standard Assessment',
        blurb: 'Link a standardised assessment',
    },
];

/**
 * Which cards a plan may use.
 *
 * Read off the SNW-{P0|P1|LC}-My Schools-LinkPrograms mock-ups:
 *
 *   P0  — only Standard Assessment enabled (Learning Program greyed out)
 *   P1  — Learning Program and Standard Assessment both enabled
 *         (P1A: programs created by the partner; P1B: created by the network —
 *         same card either way)
 *   LC  — Learning Program enabled (self-authored via LearnCreator),
 *         Standard Assessment greyed out
 *
 * P1A and P1B are P1 variants and follow P1, the same way the navigation
 * solution map treats them.
 *
 * A plan the deck says nothing about gets everything: this dialog decides what
 * to OFFER, and the dialogs behind it still enforce their own rules.
 */
const ENABLED_BY_PLAN = {
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P0]:  [LINK_TYPE.STANDARD_ASSESSMENT],
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1]:  [LINK_TYPE.LEARN_CREATOR, LINK_TYPE.LEARNING_PROGRAM, LINK_TYPE.STANDARD_ASSESSMENT],
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1A]: [LINK_TYPE.LEARN_CREATOR, LINK_TYPE.LEARNING_PROGRAM, LINK_TYPE.STANDARD_ASSESSMENT],
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1B]: [LINK_TYPE.LEARN_CREATOR, LINK_TYPE.LEARNING_PROGRAM, LINK_TYPE.STANDARD_ASSESSMENT],
    [DQ_PRO_PLAN_KEY.ENTERPRISE_LC]:  [LINK_TYPE.LEARN_CREATOR, LINK_TYPE.LEARNING_PROGRAM],
};

// A direct plan offers what its enterprise plan offers (solutionPlanKey).
export const enabledLinkTypes = (planKey) =>
    ENABLED_BY_PLAN[solutionPlanKey(planKey)] || CARDS.map((c) => c.key);

/**
 * "What would you like to link?" — PW deck slides 48-50, the dialog behind the
 * "Link Programs" row action on slide 34.
 *
 * @param {Object} unavailableReasons  { [LINK_TYPE]: reason } for cards the plan
 *   offers but that cannot be acted on right now; rendered greyed, reason on hover.
 * @param {Array<string>} cardKeys  Which LINK_TYPE cards to show — defaults to
 *   all three. The "Link Products" row action (slide 34, row 4) reuses this
 *   same dialog scoped to just LINK_TYPE.LEARN_CREATOR, since "Link Programs"
 *   (row 5) now covers only Learning Program / Standard Assessment.
 */
const LinkTypePickerDialog = ({
    open,
    onClose,
    onChoose,
    planKey,
    orgName,
    unavailableReasons = {},
    cardKeys,
    // "product" for the Link Products flow (slide 49-50), "program" for Link
    // Programs (slide 53-55) — the subtitle wording differs between the two.
    assignNoun = 'program',
    // What the card blurbs call the row being linked: school, school network or class.
    targetNoun = 'class',
}) => {
    const allowed = enabledLinkTypes(planKey);
    const cards = cardKeys ? CARDS.filter((c) => cardKeys.includes(c.key)) : CARDS;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: '20px', backgroundColor: 'var(--brand-surface, #fff)' } }}>
            <DialogContent sx={{ pt: 4, pb: 5, px: { xs: 2, sm: 5 }, position: 'relative' }}>
                <IconButton
                    onClick={onClose}
                    aria-label="Close"
                    sx={{ position: 'absolute', top: 12, right: 12, color: MUTED }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>

                <Typography sx={{ fontSize: '26px', fontWeight: 800, textAlign: 'center' }}>
                    What would you like to link?
                </Typography>
                <Typography sx={{ fontSize: '15px', color: MUTED, textAlign: 'center', mt: 0.75, mb: 4 }}>
                    {/* The word for what is being linked to — school, school network or
                        class (Ian-PW,SNW,SW-19Oct-1); products keep their own wording. */}
                    {assignNoun === 'program'
                        ? `Choose the type of program to link to this ${targetNoun}`
                        : `Choose the type of ${assignNoun} you would like to assign`}
                </Typography>
                {orgName && (
                    <Typography sx={{ fontSize: '13px', color: MUTED, textAlign: 'center', mt: -3, mb: 3 }}>
                        {orgName}
                    </Typography>
                )}

                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.min(cards.length, 3)}, 1fr)` },
                    gap: 2.5,
                    maxWidth: cards.length === 1 ? '320px' : '100%',
                    mx: cards.length === 1 ? 'auto' : 0,
                }}>
                    {cards.map((card) => {
                        const offeredByPlan = allowed.includes(card.key);
                        const blocked = !!unavailableReasons[card.key];
                        const disabled = !offeredByPlan || blocked;
                        const reason = !offeredByPlan
                            ? 'Not included in your plan.'
                            : (blocked ? unavailableReasons[card.key] : '');

                        const tile = (
                            <Box
                                onClick={disabled ? undefined : () => onChoose(card.key)}
                                role="button"
                                aria-disabled={disabled}
                                tabIndex={disabled ? -1 : 0}
                                onKeyDown={(e) => {
                                    if (disabled) return;
                                    if (e.key === ' ' || e.key === 'Enter') {
                                        e.preventDefault();
                                        onChoose(card.key);
                                    }
                                }}
                                sx={{
                                    border: `1px solid ${BORDER}`,
                                    borderRadius: '16px',
                                    padding: '28px 20px 32px',
                                    textAlign: 'center',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    backgroundColor: 'var(--brand-surface, #fffdfb)',
                                    opacity: disabled ? 0.45 : 1,
                                    transition: 'border-color 120ms, transform 120ms',
                                    '&:hover': disabled ? {} : { borderColor: ORANGE, transform: 'translateY(-2px)' },
                                }}
                            >
                                <Box sx={{
                                    width: 72, height: 72, borderRadius: '20px', mx: 'auto', mb: 2.5,
                                    backgroundColor: 'rgba(255,105,0,0.12)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <Box sx={{ width: 30, height: 30, borderRadius: '8px', backgroundColor: ORANGE }} />
                                </Box>
                                <Typography sx={{ fontSize: '18px', fontWeight: 800 }}>
                                    {card.title}
                                </Typography>
                                <Typography sx={{ fontSize: '14px', color: MUTED, mt: 1.25 }}>
                                    {card.blurb.replace('{target}', targetNoun)}
                                </Typography>
                            </Box>
                        );

                        // A disabled tile still explains itself on hover, rather
                        // than being an inert rectangle.
                        return (
                            <Box key={card.key}>
                                {reason
                                    ? <Tooltip title={reason} arrow><span>{tile}</span></Tooltip>
                                    : tile}
                            </Box>
                        );
                    })}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default LinkTypePickerDialog;
